import { Box, Button, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { SortDirection, useFinancialReservesQuery } from 'generated/graphql';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { FintelligenceReportCashFlowAdjustmentDialog } from '../FintelligenceReportCashFlowAdjustmentDialog/FintelligenceReportCashFlowAdjustmentDialog';
import { FintelligenceReportIntroDialog } from '../FintelligenceReportIntroDialog/FintelligenceReportIntroDialog';
import { AddFinancialReserveDialog } from './AddFinancialReserveDialog/AddFinancialReserveDialog';
import { FinancialReserveListItem } from './FinancialReserveListItem/FinancialReserveListItem';
import { styles } from './FintelligenceReportFinancialReserveDialog.styles';

const FintelligenceReportFinancialReserveDialogContent: React.FC<DialogComponentProps<void>> = ({ onResolve }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);

  const { data: financialReservesData } = useFinancialReservesQuery({
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated,
    variables: {
      sortInput: {
        by: [{ direction: SortDirection.Desc, fieldPath: 'amount.amount' }],
      },
    },
  });

  const addFinancialReserveDialog = useDialog<DialogComponentProps<void>, void>(AddFinancialReserveDialog, {
    instanceId: 'add-financial-reserve',
    navigable: true,
  });

  const handleAddFinancialReserve = async () => {
    await addFinancialReserveDialog.create({});
  };

  const fintelligenceReportCashFlowAdjustmentDialog = useDialog<DialogComponentProps<void>, void>(
    FintelligenceReportCashFlowAdjustmentDialog,
    {
      instanceId: 'fintelligence-report-cash-flow-adjustment-dialog',
      navigable: true,
    },
  );

  const handleContinue = async () => {
    await onResolve();
    fintelligenceReportCashFlowAdjustmentDialog.create({});
  };

  const fintelligenceReportIntroDialog = useDialog<DialogComponentProps<void>, void>(FintelligenceReportIntroDialog, {
    instanceId: 'fintelligence-report-intro-dialog',
    navigable: true,
  });

  const handleBack = async () => {
    await onResolve();
    fintelligenceReportIntroDialog.create({});
  };

  return (
    <Box>
      <Stack alignItems="center" spacing={1}>
        <Typography color="textSecondary" variant="bodyM">
          {t('fintelligenceReport.financialReserveDialog.step')}
        </Typography>
        <Typography textAlign="center" variant="headlineXL">
          {t('fintelligenceReport.financialReserveDialog.title')}
        </Typography>
        <Typography textAlign="center" variant="bodyL">
          {t('fintelligenceReport.financialReserveDialog.text')}
        </Typography>
      </Stack>

      <Stack mt={4} sx={styles.container}>
        {financialReservesData?.financialReserves?.items.map((value, index) => (
          <FinancialReserveListItem key={index} financialReserve={value} />
        ))}
        <Box onClick={handleAddFinancialReserve} sx={styles.buttonRow}>
          <Stack direction="row" flexGrow={1} justifyContent="space-between">
            <Typography color="blue.dark" variant="bodyL">
              {t('fintelligenceReport.financialReserveDialog.addFinancialReserve')}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Stack mt={5} spacing={1}>
        <Button color="primary" fullWidth onClick={handleContinue} size="large">
          <Typography color="white" variant="bodyL">
            {t('fintelligenceReport.financialReserveDialog.continue')}
          </Typography>
        </Button>
        <Button color="tertiaryButton" fullWidth onClick={handleBack} size="large">
          <Typography color="white" variant="bodyL">
            {t('common:back')}
          </Typography>
        </Button>
      </Stack>
    </Box>
  );
};

export const FintelligenceReportFinancialReserveDialog: React.FC<DialogComponentProps<void>> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <FintelligenceReportFinancialReserveDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
